import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import DraggableModal from '../../components/ui/DraggableModal';
import { adminFetch } from '../../utils/adminFetch';
import { TestimonialAvatar } from '../../components/TestimonialAvatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const serviceTypes = ['Bebés', 'Bodas', 'Primera Comunión', 'Embarazo', 'Publicidad', 'Familia'];

export const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ client_name: '', client_image: '', content: '', rating: 5, service_type: serviceTypes[0], date: '' });

  useEffect(() => { fetchTestimonials(); }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await adminFetch(`${API}/testimonials`);
      setTestimonials(await response.json());
    } catch (error) {
      toast.error('Error al cargar testimonios');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (testimonial = null) => {
    if (testimonial) {
      setEditing(testimonial);
      setForm({ ...testimonial });
    } else {
      setEditing(null);
      setForm({ client_name: '', client_image: '', content: '', rating: 5, service_type: serviceTypes[0], date: new Date().toISOString().split('T')[0] });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.content) { toast.error('Completa los campos obligatorios'); return; }
    try {
      const url = editing ? `${API}/admin/testimonials/${editing.testimonial_id}` : `${API}/admin/testimonials`;
      const response = await adminFetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!response.ok) throw new Error('Error saving');
      toast.success(editing ? 'Testimonio actualizado' : 'Testimonio creado');
      setShowModal(false);
      fetchTestimonials();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este testimonio?')) return;
    try {
      await adminFetch(`${API}/admin/testimonials/${id}`, { method: 'DELETE' });
      toast.success('Testimonio eliminado');
      fetchTestimonials();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl mb-2">Testimonios</h1>
            <p className="text-muted-foreground">Gestiona las opiniones de clientes</p>
          </div>
          <Button onClick={() => openModal()} className="gap-2">
            <Plus className="w-4 h-4" /> Nuevo Testimonio
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2].map(i => <div key={i} className="h-40 bg-muted animate-pulse" />)}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {testimonials.map((t) => (
              <div key={t.testimonial_id} className="bg-card border border-border p-4 group">
                <div className="flex items-start gap-4">
                  <TestimonialAvatar src={t.client_image} name={t.client_name} size="sm" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{t.client_name}</span>
                      <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < t.rating ? 'text-amber-500 fill-current' : 'text-muted'}`} />)}</div>
                    </div>
                    <span className="text-xs text-muted-foreground" data-testid={`admin-testimonial-meta-${t.testimonial_id}`}>
                      {t.service_type}
                      {(t.date || t.created_at) && ` · ${new Date(t.date || t.created_at).toLocaleDateString('es-ES')}`}
                    </span>
                    <p className="text-sm mt-2 line-clamp-3">{t.content}</p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openModal(t)} className="p-1 hover:bg-muted rounded"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(t.testimonial_id)} className="p-1 hover:bg-muted rounded"><Trash2 className="w-4 h-4 text-destructive" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {showModal && (
        <DraggableModal onClose={() => setShowModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-background border border-border w-[28rem] max-w-[calc(100vw-2rem)]">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium">{editing ? 'Editar Testimonio' : 'Nuevo Testimonio'}</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Nombre del cliente *</label>
                <Input value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">URL foto cliente</label>
                <Input value={form.client_image} onChange={e => setForm({...form, client_image: e.target.value})} placeholder="https://..." />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Opinión *</label>
                <Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={4} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Servicio</label>
                  <select value={form.service_type} onChange={e => setForm({...form, service_type: e.target.value})} className="w-full h-10 px-3 border border-input bg-background rounded-md">
                    {serviceTypes.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Valoración</label>
                  <select value={form.rating} onChange={e => setForm({...form, rating: parseInt(e.target.value)})} className="w-full h-10 px-3 border border-input bg-background rounded-md">
                    {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} estrellas</option>)}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit">Guardar</Button>
              </div>
            </form>
          </motion.div>
        </DraggableModal>
      )}
    </div>
  );
};

export default AdminTestimonials;
